/**
 * @file  contract/index.ts
 * @description  function library for the contract api
 *
 * @author ogarcia (ozkary)
 *
 */
import {
  Contract,
  ContractRequest,
  ContractNumberRequest,
  ContractNumber,
  ContractDetails
} from './contract.model';
import { Finance, FinanceRequest } from './finance.models';
import * as api from '../index';
import { formatZipCode } from '../../services/zipcode';

/**
 * gets the customer contracts
 * @param id internal customer id
 */
export const getContracts = async (id: string): Promise<Contract[]> => {
  const { get } = api;
  const result: Contract[] = await get<Contract[]>(`contract/${id}`);
  return result;
};

/**
 * sets a contract document in storage
 * @param type the rates type to fetch ppm/vsc
 */
export const setContract = async (data: Contract): Promise<Contract> => {
  const { post } = api;

  // map errors to messages
  if (data.errors) {
    data.messages = data.errors;
  }

  const result: Contract = await post<Contract>('contract', data);
  return result;
};

/**
 * sends a request to get a contract
 * @param type the rates type to fetch ppm/vsc
 */
export const submitContract = async (
  data: ContractRequest
): Promise<Contract> => {
  const { submit } = api;
  const result: Contract = await submit<ContractRequest, Contract>(
    'submit-contract',
    data
  );
  return result;
};

/**
 * gets a new contract number to use for payment and contract submit operations
 * @param data the program prefix and value
 */
export const getContractNumber = async (
  data: ContractNumberRequest
): Promise<ContractNumber> => {
  const { submit } = api;
  const result: ContractNumber = await submit<
    ContractNumberRequest,
    ContractNumber
  >('contract-number', data);
  return result;
};

/**
 * Gets an existent contract details
 * @param contractNumber the contract information to load
 * @returns the existent contact information
 */
export const getContractDetails = async (
  contractNumber: string
): Promise<ContractDetails> => {
  const { get } = api;
  const result: ContractDetails = await get<ContractDetails>(
    `contract-details/${contractNumber}`
  );
  const phone = result?.customer?.phone;
  if (!/\d{3}-\d{3}-\d{4}/.test(phone)) {
    result.customer.phone = phone.replace(
      /^(\d{3})(\d{3})(\d{4})$/g,
      '$1-$2-$3'
    );
  }

  const zipCodeVehicle = result?.vehicle?.zip;
  if (zipCodeVehicle) {
    result.vehicle.zip = formatZipCode(zipCodeVehicle);
  }

  const zipCodeCustomer = result?.customer?.homeAddress?.zip;
  if (zipCodeCustomer) {
    result.customer.homeAddress.zip = formatZipCode(zipCodeCustomer);
  }

  return result;
};

/**
 * Get financial details which can be used to set fees, downpayment information
 * @param saleDate today's date
 * @param amount Amount to filter fees. No amount multiple fees are returned
 * @returns The financial details. Status = true is a valid response
 */
export const getFinanceDetails = async (
  saleDate: string,
  amount?: number
): Promise<Finance> => {
  const { submit } = api;
  const data = { saleDate, amount } as FinanceRequest;

  let result = {
    status: { message: 'Feature not enabled', value: false },
    product: {}
  } as Finance;

  if (process.env.REACT_APP_FINANCE === 'true') {
    result = await submit<FinanceRequest, Finance>('finance-details', data);
  }
  return result;
};
